import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getOptionalPublicConfigValue,
  getRequiredPublicConfigValue,
  loadPublicConfig,
} from "config/publicConfig";

const RuntimeConfigContext = createContext(null);

export const RuntimeConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isActive = true;

    const loadConfig = async () => {
      try {
        const runtimeConfig = await loadPublicConfig();

        if (!isActive) {
          return;
        }

        setConfig(runtimeConfig);
        setError(null);
      } catch (loadError) {
        if (!isActive) {
          return;
        }

        setConfig(null);
        setError(loadError);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadConfig();

    return () => {
      isActive = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      config,
      isLoading,
      error,
      getOptionalValue: (key) =>
        typeof config?.[key] === "string" ? config[key] : null,
      getRequiredValue: (key) => {
        const value = typeof config?.[key] === "string" ? config[key] : null;

        if (!value) {
          throw new Error(`Missing required runtime config: ${key}`);
        }

        return value;
      },
    }),
    [config, error, isLoading],
  );

  return (
    <RuntimeConfigContext.Provider value={value}>
      {children}
    </RuntimeConfigContext.Provider>
  );
};

export const useRuntimeConfig = () => {
  const context = useContext(RuntimeConfigContext);

  if (!context) {
    throw new Error("useRuntimeConfig must be used within a RuntimeConfigProvider");
  }

  return context;
};

export {
  getOptionalPublicConfigValue,
  getRequiredPublicConfigValue,
  loadPublicConfig,
};

export default RuntimeConfigContext;
